import React, { useEffect } from 'react';
import { InjectedRouter } from 'react-router';
import { groupBy } from 'lodash';
import { isAfter, subMinutes } from 'date-fns';
import Button from 'components/Button';
import EventTracking from 'lib/EventTracking';
import TextWithIcon from 'components/TextWithIcon';

import CoachingHomeWelcomeHeader from 'coaching/components/CoachingHomeView/CoachingHomeWelcomeHeader';
import CoachingConstants from 'coaching/CoachingConstants';
import CoachingView, { HeaderType } from 'coaching/components/CoachingView';
import CoachingHomeParticipantSectionHeader from 'coaching/components/CoachingHomeParticipantSectionHeader';
import CoachingSessionsBySubjectContainer from 'coaching/components/CoachingHomeView/CoachingSessionsBySubjectContainer';

import {
  GetCoachingHomeDashboard_coaching_coachingHomeDashboard_participants,
  GetCoachingHomeDashboard_coaching_coachingHomeDashboard,
  GetCoachingHomeDashboard_coaching_coachingHomeDashboard_participants_participantMetadata,
} from 'coaching/queries/__generated__/GetCoachingHomeDashboard';
import CurrentUser from 'auth/CurrentUser';
import { SelfScheduleOptions } from 'coaching/components/CoachingHomeView/BookNewCoachingSessionSidebar';
import { TimeslotFrequencyInput } from 'globalTypes';
import { shouldShowOrganizationSchedulingOptions } from 'utils/coachingOrganizationHelpers';

export type Props = {
  coachingHomeDashboard: GetCoachingHomeDashboard_coaching_coachingHomeDashboard;
  router: InjectedRouter;
  headerType?: HeaderType;
};

export const coachingHomeEventPrefix = 'coaching.coaching_home_dashboard';

export default function CoachingHomeView({ coachingHomeDashboard, router, headerType }: Props) {
  useEffect(() => {
    if (coachingHomeDashboard.participants.length === 0) {
      router.push('/');
      return;
    }

    EventTracking.trackKey(`${coachingHomeEventPrefix}.view`);
  }, []);

  if (coachingHomeDashboard.participants.length === 0) {
    return null;
  }

  const user = CurrentUser.getUser();

  return (
    <CoachingView
      helpButtonEventKey={`${coachingHomeEventPrefix}.get_help.click`}
      headerType={headerType}>
      <CoachingHomeWelcomeHeader
        firstName={user.getFirstName()}
        hasUpcomingSessions={coachingHomeDashboard.participants.some(
          p => p.upcomingCoachingSessionInstances.length > 0
        )}
        data-test="welcome-header"
      />
      {coachingHomeDashboard.participants.map(participant => (
        <div key={participant.uuid} data-test={`participant-section-${participant.uuid}`}>
          <CoachingHomeParticipantSectionHeader participant={participant} />
          {getSessionsCards(participant, user, getSelfScheduleOptions(participant.participantMetadata))}
        </div>
      ))}
      {isParent(coachingHomeDashboard) && (
        <Button
          eventKey={`${coachingHomeEventPrefix}.add_a_student.click`}
          data-test="add-a-student-button"
          size="small"
          to={CoachingConstants.COACHING_PURCHASE_REVIEW_ROUTE}>
          <TextWithIcon icon="plus-circle">Add a student</TextWithIcon>
        </Button>
      )}
    </CoachingView>
  );
}

/**
 * If _any_ of the student uuids for the participants do NOT equal the passed in UUID, then we're
 * looking at someone who's a parent.
 * This is a bit convoluted and would be better served with a "isParent" code
 */
function isParent(coachingHomeDashboard: GetCoachingHomeDashboard_coaching_coachingHomeDashboard) {
  return (
    coachingHomeDashboard.participants.find(participant => {
      return participant.participantMetadata.studentUuid !== coachingHomeDashboard.uuid;
    }) !== undefined
  );
}

function getUpcomingInstancesGroupedBySubjectName(
  participant: GetCoachingHomeDashboard_coaching_coachingHomeDashboard_participants
) {
  const upcomingInstances = participant.upcomingCoachingSessionInstances.filter(i =>
    // we should count an instance as upcoming even if it's currently in progress,
    // so check that the startDateTime of the instance is after the current time
    // minus the duration of the session
    isAfter(i.startDateTime, subMinutes(new Date(), parseInt(i.duration)))
  );
  return groupBy(upcomingInstances, instance => instance.subject.name);
}

function getRecurringSessionSchedulesGroupedBySubjectName(
  participant: GetCoachingHomeDashboard_coaching_coachingHomeDashboard_participants
) {
  const recurringSchedules = participant.coachingSessionSchedules.filter(s => s.isRecurring);
  return groupBy(recurringSchedules, schedule => schedule.subject.name);
}

/**
 * Gathers all the individual sessions this user has.
 * If a participant has no sessions, return a session info container with an empty coaching session.
 * We also want to hide the free trial sessions after a user has completed a free trial session and has scheduled more
 * sessions.
 */
function getSessionsCards(
  participant: GetCoachingHomeDashboard_coaching_coachingHomeDashboard_participants,
  user: any,
  selfScheduleOptions: SelfScheduleOptions
) {
  const recurringSessionSchedules = getRecurringSessionSchedulesGroupedBySubjectName(participant);
  const upcomingInstancesBySubject = getUpcomingInstancesGroupedBySubjectName(participant);
  const subjectsParticipantHasStudied = participant.sessionTools.map(s => s.subject.name);
  const {
    enrolledInOrganizationTutoring,
    organizationTutoring,
  } = participant.participantMetadata;
  const showOrganizationSchedulingOptions = shouldShowOrganizationSchedulingOptions({
    enrolledInOrganizationTutoring,
    organizationTutoring,
  });
  const organizationTutoringMetadata = {
    enrolledInOrganizationTutoring,
    showOrganizationSchedulingOptions,
  };

  if (subjectsParticipantHasStudied.length === 0) {
    return (
      <CoachingSessionsBySubjectContainer
        organizationTutoringMetadata={organizationTutoringMetadata}
        recurringSessionSchedules={[]}
        upcomingInstances={[]}
        previousCoaches={participant.previousCoaches}
        participantMetadata={participant.participantMetadata}
        hasStudentInvitation={!!participant.studentInvitation}
        userUuid={user.getUuid()}
        selfScheduleOptions={selfScheduleOptions}
      />
    );
  }

  return subjectsParticipantHasStudied.map(subjectName => {
    const sessionSchedules = recurringSessionSchedules[subjectName];
    const upcomingInstances = upcomingInstancesBySubject[subjectName];
    const sessionTools = participant.sessionTools.find(tools => tools.subject.name === subjectName);
    return (
      <CoachingSessionsBySubjectContainer
        organizationTutoringMetadata={organizationTutoringMetadata}
        key={subjectName}
        recurringSessionSchedules={sessionSchedules}
        upcomingInstances={upcomingInstances}
        previousCoaches={participant.previousCoaches}
        participantMetadata={participant.participantMetadata}
        subjectName={subjectName}
        sessionTools={sessionTools}
        hasStudentInvitation={!!participant.studentInvitation}
        userUuid={user.getUuid()}
        selfScheduleOptions={selfScheduleOptions}
      />
    );
  });
}

function getSelfScheduleOptions(
  participant: GetCoachingHomeDashboard_coaching_coachingHomeDashboard_participants_participantMetadata,
): SelfScheduleOptions {
  //todo: replace this with the real thing when we add it to the dashboardQuery
  const orgTutoringOptions: SelfScheduleOptions = {
    durationOptions: [60],
    frequencyOptions: [TimeslotFrequencyInput.weekly],
  };
  const defaultOptions: SelfScheduleOptions = {
    durationOptions: CoachingConstants.COACHING_SESSION_DURATIONS,
    frequencyOptions: [
      TimeslotFrequencyInput.weekly,
      TimeslotFrequencyInput.everyOtherWeek,
      TimeslotFrequencyInput.once,
    ],
  };
  return participant.enrolledInOrganizationTutoring ? orgTutoringOptions : defaultOptions;
}
